import { useState, useEffect, useRef, useCallback } from 'react'
import { fillSchedule, chatSchedule } from '@/utils/fillSchedule'
import { useTime } from '@/contexts/TimeContext'
import { isCalendarAvailable, fetchCalendarEvents, fetchAllDayEvents } from '@/utils/calendarEvents'
import { detectCalendarConflicts } from '@/utils/scheduleConflicts'
import { playSubtaskCheck } from '@/utils/feedback'
import { useSpeechToText } from '@/hooks/useSpeechToText'
import useResizablePanel from '@/hooks/useResizablePanel'
import SwipeableRow from './SwipeableRow'
import ChatPanel from './ChatPanel'
import SparklesIcon from './icons/SparklesIcon'
import { ScheduleSkeleton } from './Skeleton'

const JS_DAY_TO_LABEL = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function formatTime12(time24) {
  const [h, m] = time24.split(':').map(Number)
  const ampm = h >= 12 ? 'PM' : 'AM'
  const h12 = h % 12 || 12
  return `${h12}:${m.toString().padStart(2, '0')} ${ampm}`
}

function formatDateShort(dateStr) {
  const date = new Date(dateStr + 'T00:00:00')
  return date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })
}

function toDateStr(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

// Convert "HH:MM" to minutes since midnight
function timeToMinutes(time24) {
  const [h, m] = time24.split(':').map(Number)
  return h * 60 + m
}

const TYPE_ICONS = {
  class: '📚',
  meal: '🍽',
  break: '☕',
  task: '✏️',
}

function CalendarDayIcon({ day }) {
  return (
    <span className="calendar-day-icon" aria-label={`Calendar day ${day}`}>
      <span className="calendar-day-icon-header" />
      <span className="calendar-day-icon-date">{day}</span>
    </span>
  )
}

function DailySchedule({ classes, tasks, onToggleSubtask, onNavigate, defaultDayStart, defaultDayEnd, savedSchedule, onSaveSchedule, loadScheduleByDate }) {
  const { now: getNow } = useTime()

  // The date the currently displayed plan is for (YYYY-MM-DD)
  const [planDate, setPlanDate] = useState(null)
  // Schedule state
  const [schedule, setSchedule] = useState([])
  const [lockedIndices, setLockedIndices] = useState(new Set())
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [generated, setGenerated] = useState(false)
  const [dayStart, setDayStart] = useState(defaultDayStart || '08:00')
  const [dayEnd, setDayEnd] = useState(defaultDayEnd || '17:00')
  const [saving, setSaving] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [showConfirmSave, setShowConfirmSave] = useState(false)
  const [now, setNow] = useState(getNow)

  // Calendar integration state
  const [calendarEvents, setCalendarEvents] = useState([])
  const [allDayEvents, setAllDayEvents] = useState([])
  const [calendarLoading, setCalendarLoading] = useState(false)
  const [calendarError, setCalendarError] = useState(false)
  const [conflictIndices, setConflictIndices] = useState(new Set())
  const calendarSyncEnabled = (() => {
    try { return localStorage.getItem('adhd-calendar-sync-enabled') === 'true' } catch { return false }
  })()

  // Track which date the savedSchedule was already loaded for
  const loadedSavedDateRef = useRef(null)

  // Auto-scroll to active schedule item on dashboard navigation
  const activeItemRef = useRef(null)
  const hasScrolledRef = useRef(false)
  // Store the last saved schedule snapshot so we can detect changes and discard
  const [lastSavedSnapshot, setLastSavedSnapshot] = useState(null)

  // Chat state
  const [chatOpen, setChatOpen] = useState(false)
  const [chatMessages, setChatMessages] = useState([])
  const [chatInput, setChatInput] = useState('')
  const [chatLoading, setChatLoading] = useState(false)
  const { isListening, startListening, stopListening, isSupported: micSupported, interimText } = useSpeechToText(
    useCallback((text) => setChatInput((prev) => prev ? prev + ' ' + text : text), [])
  )
  const { chatWidth, containerRef, onPointerDown } = useResizablePanel()

  // Update current time every 60 seconds
  useEffect(() => {
    const interval = setInterval(() => setNow(getNow()), 60000)
    return () => clearInterval(interval)
  }, [getNow])

  // Also update immediately when getNow identity changes (time simulation changed)
  useEffect(() => {
    setNow(getNow())
  }, [getNow])

  const currentDateStr = toDateStr(now)

  // Initialize planDate to today on mount
  useEffect(() => {
    if (!planDate) {
      setPlanDate(currentDateStr)
    }
  }, [currentDateStr, planDate])

  // Pre-populate from saved schedule on mount
  useEffect(() => {
    if (savedSchedule && savedSchedule.schedule?.length > 0 && loadedSavedDateRef.current !== savedSchedule.scheduleDate) {
      setSchedule(savedSchedule.schedule)
      setDayStart(savedSchedule.dayStart)
      setDayEnd(savedSchedule.dayEnd)
      setGenerated(true)
      loadedSavedDateRef.current = savedSchedule.scheduleDate
      // Pin all tasks and store snapshot
      const taskIndices = new Set()
      savedSchedule.schedule.forEach((item, idx) => {
        if (item.type === 'task') taskIndices.add(idx)
      })
      setLockedIndices(taskIndices)
      setLastSavedSnapshot({
        schedule: savedSchedule.schedule,
        dayStart: savedSchedule.dayStart,
        dayEnd: savedSchedule.dayEnd,
      })
    }
  }, [savedSchedule])

  // Midnight detection: when the real date changes past the planDate, reset
  useEffect(() => {
    if (!planDate) return
    // If we're showing a plan for a date that is now in the past (date rolled over midnight)
    if (currentDateStr > planDate) {
      // Reset to fresh state for the new day
      setPlanDate(currentDateStr)
      setSchedule([])
      setLockedIndices(new Set())
      setConflictIndices(new Set())
      setGenerated(false)
      setError(null)
      setDayStart(defaultDayStart || '08:00')
      setDayEnd(defaultDayEnd || '17:00')
      setSaveSuccess(false)
      loadedSavedDateRef.current = null
      setLastSavedSnapshot(null)
    }
  }, [currentDateStr, planDate, defaultDayStart, defaultDayEnd])

  // Auto-scroll to the active schedule item when the dashboard is first shown
  useEffect(() => {
    if (hasScrolledRef.current || schedule.length === 0) return
    hasScrolledRef.current = true

    requestAnimationFrame(() => {
      if (activeItemRef.current) {
        activeItemRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    })
  }, [schedule])

  const nowMinutes = now.getHours() * 60 + now.getMinutes()
  const isTodayPlan = planDate === currentDateStr
  const isPastDayEnd = generated && isTodayPlan && nowMinutes >= timeToMinutes(dayEnd)
  // When showing tomorrow's plan, the day hasn't started yet
  const isTomorrowPlan = planDate > currentDateStr

  const planDateObj = planDate ? new Date(planDate + 'T00:00:00') : new Date()
  const planDayLabel = JS_DAY_TO_LABEL[planDateObj.getDay()]

  // Get classes for the plan's date
  const planClasses = classes
    .filter((c) => c.days.includes(planDayLabel))
    .sort((a, b) => a.startTime.localeCompare(b.startTime))

  // Fetch native calendar events (timed + all-day) when calendar sync is enabled
  useEffect(() => {
    if (!calendarSyncEnabled || !isCalendarAvailable() || !planDate) {
      setCalendarEvents([])
      setAllDayEvents([])
      setCalendarError(false)
      return
    }

    let cancelled = false
    setCalendarLoading(true)

    Promise.all([
      fetchCalendarEvents(planDate, dayStart, dayEnd),
      fetchAllDayEvents(planDate),
    ]).then(([events, allDay]) => {
      if (!cancelled) {
        setCalendarEvents(events)
        setAllDayEvents(allDay)
        setCalendarError(false)
        setCalendarLoading(false)
      }
    }).catch(() => {
      if (!cancelled) {
        setCalendarEvents([])
        setAllDayEvents([])
        setCalendarError(true)
        setCalendarLoading(false)
      }
    })

    return () => { cancelled = true }
  }, [planDate, dayStart, dayEnd, calendarSyncEnabled])

  // Merge fresh calendar events into a loaded (saved) schedule
  const calendarMergedRef = useRef(null)
  useEffect(() => {
    if (!generated || calendarEvents.length === 0) return
    // Only merge once per planDate to avoid loops
    if (calendarMergedRef.current === planDate) return
    // Don't merge if schedule already has event items
    if (schedule.some(item => item.type === 'event')) return

    calendarMergedRef.current = planDate
    const merged = [...schedule, ...calendarEvents].sort((a, b) => a.time.localeCompare(b.time))
    setSchedule(merged)
    setConflictIndices(detectCalendarConflicts(merged))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- schedule excluded intentionally: effect sets schedule, adding it would cause infinite loop
  }, [calendarEvents, generated, planDate])

  // Re-detect conflicts whenever the schedule changes (e.g. after chat edits)
  useEffect(() => {
    if (!generated || !schedule.some(item => item.type === 'event')) {
      setConflictIndices(new Set())
      return
    }
    setConflictIndices(detectCalendarConflicts(schedule))
  }, [schedule, generated])

  // Get all incomplete subtasks across all tasks
  function getAvailableSubtasks(excludeSubtaskIds = new Set()) {
    const available = []
    for (const task of tasks) {
      const isComplete = task.subtasks.length > 0 && task.subtasks.every((st) => st.done)
      if (isComplete) continue

      for (const st of task.subtasks) {
        if (st.done) continue
        if (excludeSubtaskIds.has(st.id)) continue
        available.push({
          taskId: task.id,
          subtaskId: st.id,
          taskTitle: task.title,
          subtaskTitle: st.title,
          timeEstimate: st.timeEstimate,
          dueDate: task.dueDate,
          priority: task.priority,
        })
      }
    }
    return available
  }

  // Check if a subtask is done in the current tasks state
  function isSubtaskDone(taskId, subtaskId) {
    const task = tasks.find((t) => t.id === taskId)
    if (!task) return false
    const st = task.subtasks.find((s) => s.id === subtaskId)
    return st?.done || false
  }

  async function handleFillSchedule() {
    // On refill, check if all items are pinned (no room to replace)
    if (generated && schedule.length > 0) {
      const hasUnpinned = schedule.some((_, idx) => !lockedIndices.has(idx))
      if (!hasUnpinned) {
        setError('All items are pinned! Unpin some tasks to make room for new items.')
        return
      }
    }

    setLoading(true)
    setError(null)

    const locked = []
    const lockedSubtaskIds = new Set()
    for (const idx of lockedIndices) {
      if (schedule[idx]) {
        locked.push(schedule[idx])
        if (schedule[idx].subtaskId) {
          lockedSubtaskIds.add(schedule[idx].subtaskId)
        }
      }
    }

    let availableSubtasks = getAvailableSubtasks(lockedSubtaskIds)

    // On first fill (not refill), limit to top 5 tasks with 2 subtasks each for faster AI response
    if (!generated) {
      const priorityOrder = { high: 0, medium: 1, low: 2 }
      // Sort by priority then due date
      availableSubtasks.sort((a, b) => {
        const pa = priorityOrder[a.priority] ?? 1
        const pb = priorityOrder[b.priority] ?? 1
        if (pa !== pb) return pa - pb
        if (a.dueDate && b.dueDate) return a.dueDate.localeCompare(b.dueDate)
        if (a.dueDate) return -1
        if (b.dueDate) return 1
        return 0
      })

      // Group by task, pick top 5 tasks with 2 subtasks each
      const taskCounts = {}
      const taskOrder = []
      const limited = []
      for (const st of availableSubtasks) {
        if (!taskCounts[st.taskId]) {
          taskCounts[st.taskId] = 0
          taskOrder.push(st.taskId)
        }
        if (taskOrder.indexOf(st.taskId) >= 5) continue
        if (taskCounts[st.taskId] >= 2) continue
        taskCounts[st.taskId]++
        limited.push(st)
      }
      availableSubtasks = limited
    }

    try {
      const result = await fillSchedule({
        classes: planClasses.map((c) => ({
          name: c.name,
          startTime: c.startTime,
          duration: c.duration,
          location: c.location,
        })),
        calendarEvents,
        availableSubtasks,
        lockedItems: locked,
        dayStart,
        dayEnd,
      })

      setSchedule(result)
      // AI response already includes event items — mark as merged
      calendarMergedRef.current = planDate
      const newLocked = new Set()
      result.forEach((item, idx) => {
        if (lockedSubtaskIds.has(item.subtaskId)) {
          newLocked.add(idx)
        }
      })
      setLockedIndices(newLocked)
      setGenerated(true)
      setSaveSuccess(false)
    } catch (err) {
      console.error('Failed to fill schedule:', err.message)
      setError(err.message || 'Failed to generate schedule. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  async function handlePlanTomorrow() {
    const tomorrowDate = new Date(now)
    tomorrowDate.setDate(tomorrowDate.getDate() + 1)
    const tomorrowStr = toDateStr(tomorrowDate)

    // Reset state for tomorrow
    setPlanDate(tomorrowStr)
    setSchedule([])
    setLockedIndices(new Set())
    setConflictIndices(new Set())
    setGenerated(false)
    setError(null)
    setDayStart(defaultDayStart || '08:00')
    setDayEnd(defaultDayEnd || '17:00')
    setSaveSuccess(false)
    loadedSavedDateRef.current = null
    setLastSavedSnapshot(null)

    // Try to load any existing saved schedule for tomorrow
    if (loadScheduleByDate) {
      const saved = await loadScheduleByDate(tomorrowStr)
      if (saved && saved.schedule?.length > 0) {
        setSchedule(saved.schedule)
        setDayStart(saved.dayStart)
        setDayEnd(saved.dayEnd)
        setGenerated(true)
        loadedSavedDateRef.current = tomorrowStr
        // Pin all tasks and store snapshot
        const taskIndices = new Set()
        saved.schedule.forEach((item, idx) => {
          if (item.type === 'task') taskIndices.add(idx)
        })
        setLockedIndices(taskIndices)
        setLastSavedSnapshot({
          schedule: saved.schedule,
          dayStart: saved.dayStart,
          dayEnd: saved.dayEnd,
        })
      }
    }
  }

  function toggleLock(idx) {
    setLockedIndices((prev) => {
      const next = new Set(prev)
      if (next.has(idx)) {
        next.delete(idx)
      } else {
        next.add(idx)
      }
      return next
    })
  }

  function handleComplete(taskId, subtaskId) {
    playSubtaskCheck()
    onToggleSubtask(taskId, subtaskId)
  }

  async function handleSave() {
    // If a saved plan already exists for this date, ask for confirmation
    if (lastSavedSnapshot && !showConfirmSave) {
      setShowConfirmSave(true)
      return
    }
    setShowConfirmSave(false)
    setSaving(true)
    // Strip calendar events before saving — they're fetched fresh from device calendar
    const scheduleToSave = schedule.filter(item => item.type !== 'event')
    const { error: saveError } = await onSaveSchedule(planDate, dayStart, dayEnd, scheduleToSave)
    setSaving(false)
    if (saveError) {
      setError(`Save failed: ${saveError.message || JSON.stringify(saveError)}`)
    } else {
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 2000)
      // Pin all tasks and update snapshot
      const taskIndices = new Set()
      schedule.forEach((item, idx) => {
        if (item.type === 'task') taskIndices.add(idx)
      })
      setLockedIndices(taskIndices)
      setLastSavedSnapshot({
        schedule: [...schedule],
        dayStart,
        dayEnd,
      })
      // Reset chat on save
      setChatMessages([])
      setChatInput('')
      setChatOpen(false)
    }
  }

  function handleDiscard() {
    if (!lastSavedSnapshot) return
    setSchedule(lastSavedSnapshot.schedule)
    setDayStart(lastSavedSnapshot.dayStart)
    setDayEnd(lastSavedSnapshot.dayEnd)
    // Pin all tasks to match saved state
    const taskIndices = new Set()
    lastSavedSnapshot.schedule.forEach((item, idx) => {
      if (item.type === 'task') taskIndices.add(idx)
    })
    setLockedIndices(taskIndices)
    setSaveSuccess(false)
    // Reset chat on discard
    setChatMessages([])
    setChatInput('')
    setChatOpen(false)
  }

  async function handleChatSend() {
    const message = chatInput.trim()
    if (!message || chatLoading) return

    const userMsg = { role: 'user', text: message }
    const updatedMessages = [...chatMessages, userMsg]
    setChatMessages(updatedMessages)
    setChatInput('')
    setChatLoading(true)

    try {
      const result = await chatSchedule({
        userMessage: message,
        currentSchedule: schedule,
        availableSubtasks: getAvailableSubtasks(),
        classes: planClasses.map((c) => ({
          name: c.name,
          startTime: c.startTime,
          duration: c.duration,
          location: c.location,
        })),
        calendarEvents,
        dayStart,
        dayEnd,
        chatHistory: chatMessages,
      })

      const assistantMsg = { role: 'assistant', text: result.message }
      setChatMessages((prev) => [...prev, assistantMsg])

      if (result.type === 'schedule' && Array.isArray(result.schedule)) {
        setSchedule(result.schedule)
        setSaveSuccess(false)
      }
    } catch (err) {
      setChatMessages((prev) => [...prev, { role: 'assistant', text: 'Sorry, something went wrong. Please try again.' }])
      console.error('Chat schedule error:', err.message)
    } finally {
      setChatLoading(false)
    }
  }

  // Detect if schedule has changed from the last saved version
  // Filter out calendar events since they are never persisted (stripped before save)
  // If no snapshot exists yet (first save), any non-empty schedule counts as unsaved
  const scheduleWithoutEvents = schedule.filter(item => item.type !== 'event')
  const hasUnsavedChanges = lastSavedSnapshot
    ? (JSON.stringify(scheduleWithoutEvents) !== JSON.stringify(lastSavedSnapshot.schedule) ||
       dayStart !== lastSavedSnapshot.dayStart ||
       dayEnd !== lastSavedSnapshot.dayEnd)
    : scheduleWithoutEvents.length > 0

  // Determine button label and action
  function getMainButtonConfig() {
    if (loading) return { label: 'Planning...', action: handleFillSchedule, disabled: true }
    if (isPastDayEnd && !isTomorrowPlan) {
      return { label: 'Plan Tomorrow', action: handlePlanTomorrow, disabled: false }
    }
    if (generated) return { label: 'Replan Schedule', action: handleFillSchedule, disabled: false }
    return { label: 'Plan My Day', action: handleFillSchedule, disabled: false }
  }

  // Show active highlighting only for today's plan and only when the day hasn't ended
  const showActiveHighlighting = isTodayPlan

  // --- Render helpers ---
  function renderScheduleList(items, locked, onToggleLock, conflicts = new Set()) {
    return (
      <ul className="schedule-list">
        {items.map((item, idx) => {
          const isTask = item.type === 'task'
          const isLocked = locked.has(idx)
          const isDone = isTask && isSubtaskDone(item.taskId, item.subtaskId)
          const itemStart = timeToMinutes(item.time)
          const itemEnd = timeToMinutes(item.endTime)
          const isActive = showActiveHighlighting && nowMinutes >= itemStart && nowMinutes < itemEnd
          const isOverdue = showActiveHighlighting && !isDone && nowMinutes >= itemEnd
          const isConflict = conflicts.has(idx)
          // Boundary-spanning calendar events carry their true times for display;
          // everything else just uses its scheduled time.
          const displayStart = item.displayTime || item.time
          const displayEnd = item.displayEndTime || item.endTime

          // Find the name of the overlapping calendar event for the conflict label
          let conflictEventName = null
          if (isConflict) {
            for (const other of items) {
              if (other.type !== 'event') continue
              const evStart = timeToMinutes(other.time)
              const evEnd = timeToMinutes(other.endTime)
              if (itemStart < evEnd && evStart < itemEnd) {
                conflictEventName = other.title
                break
              }
            }
          }

          return (
            <li
              key={`${item.time}-${idx}`}
              ref={isActive && !isDone ? activeItemRef : null}
              className={`schedule-item schedule-item--${item.type}${isLocked ? ' schedule-item--locked' : ''}${isDone ? ' schedule-item--done' : ''}${isActive && !isDone ? ' schedule-item--active' : ''}${isOverdue && isTask ? ' schedule-item--overdue' : ''}${isConflict ? ' schedule-item--conflict' : ''} stagger-item`}
              style={{ animationDelay: `${Math.min(idx, 10) * 50}ms` }}
            >
              {isTask ? (
                <SwipeableRow
                  actions={[
                    { key: 'pin', icon: isLocked ? '📌' : '📍', label: isLocked ? 'Unpin' : 'Pin', onClick: () => onToggleLock(idx), variant: 'pin' },
                    { key: 'complete', icon: isDone ? '✓' : '○', label: isDone ? 'Undo' : 'Done', onClick: () => handleComplete(item.taskId, item.subtaskId), variant: 'complete' },
                  ]}
                >
                  <span className="schedule-time">
                    {formatTime12(displayStart)}
                  </span>
                  <span className="schedule-icon">
                    {item.type === 'event'
                      ? <CalendarDayIcon day={new Date(planDate + 'T00:00:00').getDate()} />
                      : TYPE_ICONS[item.type]}
                  </span>
                  <div
                    className={`schedule-content${onNavigate ? ' schedule-content--clickable' : ''}`}
                    onClick={onNavigate ? () => onNavigate('tasks', item.taskId) : undefined}
                  >
                    <span className={`schedule-title${isDone ? ' schedule-title--done' : ''}`}>
                      {item.title}
                    </span>
                    {item.taskId && (
                      <span className="schedule-task-parent">
                        {tasks.find((t) => t.id === item.taskId)?.title || ''}
                      </span>
                    )}
                    <span className="schedule-end-time">
                      until {formatTime12(displayEnd)}
                    </span>
                    {isOverdue && (
                      <span className="schedule-overdue-label">Not completed</span>
                    )}
                    {isConflict && (
                      <span className="schedule-conflict-label">
                        Conflicts with {conflictEventName || 'calendar event'}
                      </span>
                    )}
                  </div>
                  <div className="schedule-actions">
                    <button
                      className={`btn-icon schedule-lock-btn${isLocked ? ' schedule-lock-btn--active' : ''}`}
                      onClick={() => onToggleLock(idx)}
                      title={isLocked ? 'Unpin' : 'Pin to keep'}
                    >
                      {isLocked ? '📌' : '📍'}
                    </button>
                    <input
                      type="checkbox"
                      className="subtask-checkbox"
                      checked={isDone}
                      onChange={() => handleComplete(item.taskId, item.subtaskId)}
                      title="Mark complete"
                    />
                  </div>
                </SwipeableRow>
              ) : (
                <>
                  <span className="schedule-time">
                    {formatTime12(displayStart)}
                  </span>
                  <span className="schedule-icon">
                    {item.type === 'event'
                      ? <CalendarDayIcon day={new Date(planDate + 'T00:00:00').getDate()} />
                      : TYPE_ICONS[item.type]}
                  </span>
                  <span className="schedule-title">
                    {item.title}
                  </span>
                  <span className="schedule-end-time schedule-end-time--inline">
                    until {formatTime12(displayEnd)}
                  </span>
                </>
              )}
            </li>
          )
        })}
      </ul>
    )
  }

  function renderPinAllButton(items, locked, setLocked) {
    const taskIndices = items.map((item, i) => item.type === 'task' ? i : -1).filter(i => i !== -1)
    const allPinned = taskIndices.length > 0 && taskIndices.every(i => locked.has(i))

    return (
      <div className="daily-schedule-hint-row">
        <p className="daily-schedule-hint">
          Tap the pin icon on tasks you want to keep, then hit "Replan Schedule" to replace the rest.
        </p>
        {taskIndices.length > 0 && (
          <button
            className={`btn btn-sm schedule-pin-all-btn${allPinned ? ' schedule-pin-all-btn--active' : ''}`}
            onClick={() => {
              if (allPinned) {
                setLocked(new Set())
              } else {
                setLocked(new Set(taskIndices))
              }
            }}
          >
            {allPinned ? '📌 Unpin All' : '📍 Pin All'}
          </button>
        )}
      </div>
    )
  }

  const hasTasksToSchedule = tasks.some(
    (t) => t.subtasks.length > 0 && t.subtasks.some((st) => !st.done)
  )

  if (!hasTasksToSchedule && !generated && planClasses.length === 0) {
    return null
  }

  const mainBtn = getMainButtonConfig()

  // Determine heading
  const heading = isTomorrowPlan
    ? `Tomorrow's Plan — ${formatDateShort(planDate)}`
    : "Today's Plan"

  return (
    <section ref={containerRef} className={`daily-schedule${chatOpen && generated ? ' daily-schedule--chat-open' : ''}`}>
      <div className="daily-schedule-content">
      <div className="daily-schedule-header">
        <h3>{heading}</h3>
        <button
          className="btn btn-primary daily-schedule-btn"
          onClick={mainBtn.action}
          disabled={mainBtn.disabled}
        >
          {mainBtn.label}
        </button>
      </div>

      {isTomorrowPlan && (
        <p className="daily-schedule-tomorrow-note">
          Planning ahead — this schedule hasn't started yet. You can still check off tasks you've already completed.
        </p>
      )}

      <div className="daily-schedule-time-config">
        <label className="schedule-time-label">
          <span>Day starts</span>
          <input type="time" value={dayStart} onChange={(e) => setDayStart(e.target.value)} />
        </label>
        <label className="schedule-time-label">
          <span>Day ends</span>
          <input type="time" value={dayEnd} onChange={(e) => setDayEnd(e.target.value)} />
        </label>
      </div>

      {loading && (
        <>
          <p className="loading-text" style={{ marginBottom: '0.75rem' }}>
            AI is building your personalized daily schedule...
          </p>
          <ScheduleSkeleton count={7} />
        </>
      )}

      {error && (
        <div className="decompose-error">
          <p>{error}</p>
        </div>
      )}

      {calendarError && (
        <div className="decompose-error">
          <p>Couldn't load your calendar events. Check calendar access in Settings &rsaquo; Nudg.U &rsaquo; Calendars.</p>
        </div>
      )}

      {allDayEvents.length > 0 && (
        <div className="schedule-allday-banner">
          <span className="schedule-allday-icon" aria-hidden="true">📅</span>
          <p>All day: {allDayEvents.join(', ')}</p>
        </div>
      )}

      {!generated && !loading && planClasses.length > 0 && (
        <>
          <p className="daily-schedule-hint">Your classes for today:</p>
          {renderScheduleList(
            planClasses.map((c) => {
              const startMin = timeToMinutes(c.startTime)
              const endMin = startMin + c.duration
              const endH = String(Math.floor(endMin / 60)).padStart(2, '0')
              const endM = String(endMin % 60).padStart(2, '0')
              return {
                type: 'class',
                time: c.startTime,
                endTime: `${endH}:${endM}`,
                title: c.name + (c.location ? ` — ${c.location}` : ''),
              }
            }),
            new Set(),
            () => {}
          )}
        </>
      )}

      {!generated && !loading && calendarEvents.length > 0 && (
        <>
          <p className="daily-schedule-hint">Your calendar events:</p>
          {renderScheduleList(calendarEvents, new Set(), () => {})}
        </>
      )}

      {!generated && !loading && calendarLoading && (
        <p className="daily-schedule-hint">Loading calendar events...</p>
      )}

      {generated && !loading && schedule.length > 0 && (
        <>
          {conflictIndices.size > 0 && (
            <div className="schedule-conflict-banner">
              <p>{conflictIndices.size} item{conflictIndices.size > 1 ? 's' : ''} conflict{conflictIndices.size === 1 ? 's' : ''} with new calendar events</p>
              <button
                className="btn btn-sm"
                onClick={handleFillSchedule}
                disabled={loading}
              >
                Resolve Conflicts
              </button>
            </div>
          )}
          {renderPinAllButton(schedule, lockedIndices, setLockedIndices)}
          {renderScheduleList(schedule, lockedIndices, toggleLock, conflictIndices)}

          <div className="daily-schedule-save">
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving || !hasUnsavedChanges}
            >
              {saving ? 'Saving...' : saveSuccess ? 'Saved!' : 'Save Schedule'}
            </button>
            {!chatOpen && (
              <button
                className="btn-icon btn-chat-ai"
                onClick={() => setChatOpen(true)}
                title="Chat with AI"
                aria-label="Chat with AI"
              >
                <SparklesIcon />
              </button>
            )}
            {hasUnsavedChanges && (
              <button
                className="btn btn-secondary"
                onClick={handleDiscard}
              >
                Discard Changes
              </button>
            )}
          </div>

          {showConfirmSave && (
            <div className="modal-overlay" onClick={() => setShowConfirmSave(false)}>
              <div className="modal-card" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>Update Saved Plan?</h3>
                  <button className="btn-icon" onClick={() => setShowConfirmSave(false)}>✕</button>
                </div>
                <p style={{ padding: '0 1rem 1rem', color: 'var(--text-secondary)', margin: 0 }}>
                  A saved plan already exists for this day. Are you sure you want to update it?
                </p>
                <div style={{ display: 'flex', gap: '0.5rem', padding: '0 1rem 1rem', justifyContent: 'flex-end' }}>
                  <button className="btn btn-secondary" onClick={() => setShowConfirmSave(false)}>Cancel</button>
                  <button className="btn btn-primary" onClick={handleSave}>Yes, Update</button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {isPastDayEnd && generated && !loading && (
        <button className="plan-tomorrow-card" onClick={handlePlanTomorrow}>
          <p className="plan-tomorrow-text">Your day is done! Ready to plan tomorrow?</p>
        </button>
      )}
      </div>{/* end daily-schedule-content */}
      {chatOpen && generated && !loading && schedule.length > 0 && (
        <ChatPanel
          title="Chat with AI about your schedule"
          placeholder="Chat with desired changes to the schedule"
          messages={chatMessages}
          input={chatInput}
          onInputChange={setChatInput}
          onSend={handleChatSend}
          loading={chatLoading}
          onClose={() => setChatOpen(false)}
          chatWidth={chatWidth}
          onPointerDown={onPointerDown}
          isListening={isListening}
          startListening={startListening}
          stopListening={stopListening}
          micSupported={micSupported}
          interimText={interimText}
        />
      )}
    </section>
  )
}

export default DailySchedule
